/* ============================================================
   CrossFit W — shared subtle animations
   ============================================================ */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // --------------------------------------------------------
  // 1. Fade-up on scroll (IntersectionObserver)
  // --------------------------------------------------------
  function initFadeUp() {
    var targets = document.querySelectorAll(
      ".anim-fade-up:not(.is-visible), .hw-promo__visual:not(.is-visible), .cf-promo__visual:not(.is-visible), .bc-promo__visual:not(.is-visible), .hw-gallery-slot:not(.is-visible), .hts-checklist-item:not(.is-visible)"
    );
    if (!targets.length) return;
    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add("is-visible");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0, rootMargin: "0px 0px 200px 0px" });
    targets.forEach(function (el) { io.observe(el); });
  }

  // --------------------------------------------------------
  // 2. Counting stats — data-target, optional data-suffix
  // --------------------------------------------------------
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function animateCount(el) {
    var raw = el.getAttribute("data-target");
    if (!raw) return;
    // Extract numeric portion. Keep suffix/prefix untouched if not numeric.
    var match = String(raw).match(/-?\d+(\.\d+)?/);
    if (!match) return;
    var target = parseFloat(match[0]);
    if (isNaN(target)) return;
    var suffix = el.getAttribute("data-suffix") || "";
    var prefix = el.getAttribute("data-prefix") || "";
    var duration = 1200;
    var start = null;

    // Preserve formatting: if original target had no decimal, render integer
    var decimals = (match[0].split(".")[1] || "").length;

    function tick(ts) {
      if (!start) start = ts;
      var progress = Math.min(1, (ts - start) / duration);
      var eased = easeOutCubic(progress);
      var value = target * eased;
      el.textContent = prefix + value.toFixed(decimals) + suffix;
      if (progress < 1) requestAnimationFrame(tick);
      else el.textContent = prefix + (decimals ? target.toFixed(decimals) : target) + suffix;
    }
    requestAnimationFrame(tick);
  }

  function initCount() {
    var targets = document.querySelectorAll(".anim-count[data-target]");
    if (!targets.length) return;
    if (reduceMotion || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        var raw = el.getAttribute("data-target");
        var suffix = el.getAttribute("data-suffix") || "";
        var prefix = el.getAttribute("data-prefix") || "";
        el.textContent = prefix + raw + suffix;
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          animateCount(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.4 });
    targets.forEach(function (el) {
      el.textContent = (el.getAttribute("data-prefix") || "") + "0" + (el.getAttribute("data-suffix") || "");
      io.observe(el);
    });
  }

  // --------------------------------------------------------
  // 3. Staggered card entrance
  // --------------------------------------------------------
  function initStagger() {
    var parents = document.querySelectorAll(".anim-stagger-parent");
    if (!parents.length) return;
    if (reduceMotion || !("IntersectionObserver" in window)) {
      parents.forEach(function (p) {
        p.querySelectorAll(".anim-stagger-child").forEach(function (c) { c.classList.add("is-visible"); });
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var children = e.target.querySelectorAll(":scope > .anim-stagger-child");
        // If :scope selector not supported, fall back to all descendants
        if (!children.length) children = e.target.querySelectorAll(".anim-stagger-child");
        children.forEach(function (c, i) {
          setTimeout(function () { c.classList.add("is-visible"); }, i * 100);
        });
        io.unobserve(e.target);
      });
    }, { threshold: 0, rootMargin: "0px 0px 200px 0px" });
    parents.forEach(function (p) { io.observe(p); });
  }

  // --------------------------------------------------------
  // 4. Sticky nav shrink
  // --------------------------------------------------------
  function initStickyNav() {
    var header = document.querySelector(".header-wrapper, .section-header");
    if (!header) return;
    function onScroll() {
      if (window.scrollY > 60) header.classList.add("is-scrolled");
      else header.classList.remove("is-scrolled");
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  // --------------------------------------------------------
  // 5. Arrow nudge — wrap a trailing "→" in text-only links/buttons
  //    so CSS can nudge just the arrow on hover (see .cfw-arrow).
  //    Skipped for elements that already contain their own icon/SVG.
  // --------------------------------------------------------
  function initArrowWrap() {
    var els = document.querySelectorAll("a, button");
    els.forEach(function (el) {
      if (el.dataset.arrowWrapped || el.children.length) return;
      var text = el.textContent;
      if (!text || text.indexOf("→") === -1) return;
      if (text.trim().charAt(text.trim().length - 1) !== "→") return;
      var trimmed = text.replace(/\s*→\s*$/, "");
      el.textContent = trimmed;
      var span = document.createElement("span");
      span.className = "cfw-arrow";
      span.textContent = " →";
      el.appendChild(span);
      el.dataset.arrowWrapped = "1";
    });
  }

  // --------------------------------------------------------
  // 6. Ticker auto-scroll
  // --------------------------------------------------------
  function initTickers() {
    var tickers = document.querySelectorAll(".anim-ticker");
    tickers.forEach(function (t) {
      var inner = t.querySelector(".anim-ticker__inner");
      if (!inner || inner.dataset.duplicated === "1") return;

      // Duplicate content once for seamless loop
      inner.innerHTML += inner.innerHTML;
      inner.dataset.duplicated = "1";

      // Width-based duration — 60 px / second
      var totalWidth = inner.scrollWidth;
      var pxPerSecond = 60;
      // We scroll by -50% (half of duplicated content = original width)
      var scrollDistance = totalWidth / 2;
      var duration = Math.max(12, scrollDistance / pxPerSecond);
      inner.style.setProperty("--ticker-duration", duration + "s");
    });
  }

  // --------------------------------------------------------
  // Init
  // --------------------------------------------------------
  function init() {
    initFadeUp();
    initCount();
    initStagger();
    initStickyNav();
    initArrowWrap();
    initTickers();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Reinit on Shopify section load (theme editor + dynamic sections)
  document.addEventListener("shopify:section:load", init);

  // Safety fallback: if anything is still hidden after 2.5s (e.g. IntersectionObserver
  // missed it, JS delay, old Safari quirks), force-reveal so content is never stuck invisible.
  setTimeout(function () {
    document.querySelectorAll(".anim-fade-up:not(.is-visible), .anim-stagger-child:not(.is-visible), .hw-promo__visual:not(.is-visible), .cf-promo__visual:not(.is-visible), .bc-promo__visual:not(.is-visible), .hw-gallery-slot:not(.is-visible), .hts-checklist-item:not(.is-visible)")
      .forEach(function (el) { el.classList.add("is-visible"); });
  }, 2500);
})();
